import { Injectable } from '@angular/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { includes } from 'lodash';
import { UrlBuilder } from '../core';
import {
    Category, CategoryNaturalKey, DocumentsColumns, downloadColumn, isResponseRequiredColumn,
    letterNumberColumn, postedColumn, responseNeededByColumn, selectColumn, SiteActionsReport,
    statusColumn, titleColumn, versionDateColumn, versionNumberColumn, visitColumn, VisitReport, viewColumn
} from '../documents';
import { GroupingOptionItem, groupingOptions } from '../grouping-selector';
import { reportTypeColumn, visitCollectionDateColumn } from '../documents/documents-columns/documents-columns';
import { GridColumns } from '../shared/grid-columns';

@Injectable()
export class InvestigatorDocumentsColumns implements DocumentsColumns {
    private subjectColumn: ColDef;
    private accessionNumberColumn: ColDef;
    private readonly columnsDictionary: Map<CategoryNaturalKey, ColDef[]>;

    constructor(private urlBuilder: UrlBuilder, gridColumns: GridColumns) {
        const subjectLinkBuilder = (params: SiteActionsReportParams) =>
            this.urlBuilder
                .esa()
                .site(params.data.site)
                .subject({ zcSubjectId: params.data.zcSubjectId })
                .url();

        this.subjectColumn = gridColumns.subjectColumn(subjectLinkBuilder);

        const testResultsLinkBuilder = (params: VisitReportRendererParams) =>
            this.urlBuilder
                .esa()
                .site(params.data.site)
                .onScreenResults()
                .standardTestResults()
                .testResults()
                .url();

        this.accessionNumberColumn = gridColumns.accessionNumberColumn(testResultsLinkBuilder, data => data.accessionNumbers);

        this.columnsDictionary = this.buildColumnsDictionary();
    }

    public getColumnDefinitions(category: Category | undefined): ColDef[] {
        if (!category) {
            return [];
        }

        return this.columnsDictionary.get(category.categoryId) || [];
    }

    public hasStatusColumn(categoryId: CategoryNaturalKey) {
        return includes(this.columnsDictionary.get(categoryId), statusColumn);
    }

    public getGroupedColumns(category: string): GroupingOptionItem[] {
        if (category === 'site-actions' || category === 'lab-reports') {
            return [groupingOptions.none, groupingOptions.subject, groupingOptions.visit];
        }
        return [];
    }

    private buildColumnsDictionary(): Map<CategoryNaturalKey, ColDef[]> {
        const trainingMaterialsOthersColumns = [
            selectColumn,
            statusColumn,
            titleColumn,
            postedColumn,
            downloadColumn
        ];

        const trainingMaterialsELearningColumns = [
            statusColumn,
            titleColumn,
            postedColumn,
            viewColumn
        ];

        const siteCommunicationColumns = [
            selectColumn,
            statusColumn,
            titleColumn,
            letterNumberColumn,
            postedColumn,
            downloadColumn
        ];

        const studyManualsColumns = [
            selectColumn,
            statusColumn,
            versionNumberColumn,
            postedColumn,
            downloadColumn
        ];

        const synopsisColumns = [
            selectColumn,
            statusColumn,
            versionDateColumn,
            postedColumn,
            downloadColumn
        ];

        const labManualsOthersColumns = [
            selectColumn,
            statusColumn,
            titleColumn,
            postedColumn,
            downloadColumn
        ];

        const labReportsColumns = [
            selectColumn,
            statusColumn,
            this.subjectColumn,
            reportTypeColumn,
            visitColumn,
            visitCollectionDateColumn,
            this.accessionNumberColumn,
            postedColumn,
            downloadColumn
        ];

        const siteActionsColumns = [
            selectColumn,
            statusColumn,
            titleColumn,
            this.subjectColumn,
            this.accessionNumberColumn,
            visitColumn,
            isResponseRequiredColumn,
            responseNeededByColumn,
            postedColumn,
            downloadColumn
        ];

        return new Map<CategoryNaturalKey, ColDef[]>([
            // lab-manuals:
            ['study-manuals', studyManualsColumns],
            ['synopsis', synopsisColumns],
            ['lab-manuals-others', labManualsOthersColumns],

            // training-materials:
            ['training-materials-e-learning', trainingMaterialsELearningColumns],
            ['training-materials-others', trainingMaterialsOthersColumns],

            ['lab-reports', labReportsColumns],

            ['site-actions', siteActionsColumns],

            ['site-communications', siteCommunicationColumns],
        ]);
    }
}

interface VisitReportRendererParams extends ICellRendererParams {
    data: VisitReport;
}

type SiteActionsReportParams = {
    data: SiteActionsReport;
};